class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    // Wait for the non-blocking drawer CSS first, so an early open is never unstyled.
    this.whenStylesReady().then(() => {
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  // The drawer stylesheets are loaded with media="print" + onload (layout/theme.liquid,
  // marked data-dlux-cart-css). Resolves once every one has loaded, or after 3 s so the
  // drawer still opens if a sheet fails.
  whenStylesReady() {
    const pending = Array.from(document.querySelectorAll('link[data-dlux-cart-css]')).filter(
      (link) => !link.sheet
    );
    if (!pending.length) return Promise.resolve();
    return Promise.race([
      Promise.all(
        pending.map(
          (link) =>
            new Promise((resolve) => {
              link.addEventListener('load', resolve, { once: true });
              link.addEventListener('error', resolve, { once: true });
            })
        )
      ),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]).then(() => {
      pending.forEach((link) => {
        link.media = 'all';
      });
    });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.cartData?.sections && event.cartData.sections['cart-icon-bubble']) return;
      this.updateCartBadgeFallback();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  async updateCartBadgeFallback() {
    try {
      const response = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
      const cart = await response.json();
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      const countEl = bubble.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (countEl) {
        countEl.textContent = cart.item_count;
      } else if (cart.item_count > 0) {
        const div = document.createElement('div');
        div.className = 'cart-count-bubble';
        if (cart.item_count < 100) div.innerHTML = `<span aria-hidden="true">${cart.item_count}</span>`;
        bubble.appendChild(div);
      }
    } catch (e) {
      console.error('[cart-drawer] Fallback cart badge update failed:', e);
    }
  }

  renderContents(parsedState, options) {
    // /cart/add.js responses don't include cart-level `item_count` (only /cart.js
    // and /cart/change.js do). `!parsedState.item_count` therefore flagged the
    // drawer as empty on EVERY quick-add, hiding the header and line items.
    // An add can never empty the cart, so only flag empty on an explicit 0.
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      try {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        const newHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        if (newHTML !== null) {
          sectionElement.innerHTML = newHTML;
        }
      } catch (e) {
        console.error(`[cart-drawer] Failed to render section "${section.id}":`, e);
      }
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // options.open === false: background corrections (e.g. GWP reconcile on
      // page load) update the drawer contents without popping it open.
      if (!options || options.open !== false) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/*
 * Delegated drawer close. Replaces the inline
 * onclick="this.closest('cart-drawer').close()" attributes that used to sit on the
 * drawer close button and the empty state CTA in snippets/cart-drawer.liquid, so the
 * drawer markup carries no inline JavaScript. Bound on document, so it survives the
 * Section Rendering API replacing .drawer__inner after every cart update.
 */
document.addEventListener('click', function (evt) {
  var target = evt.target;
  if (!target || !target.closest) return;
  var trigger = target.closest('[data-dlux-cart-close]');
  if (!trigger) return;
  var drawer = trigger.closest('cart-drawer');
  if (drawer && typeof drawer.close === 'function') drawer.close();
});
